import { ICADModel, ICADOperationLibraryItem, ICADOperationEditor, ICADLayer } from "../interfaces";
import { ICADState } from "./CADContext";
import { AddEdgeAdjustmentStateEnum, EdgeAdjustmentWithoutID } from "../Components/EdgeTools/AddEdgeAdjustment";
import { nullish } from "components/OptionsWizard/WizardContext";
import { GlassLayerHelpers } from "../GlassLayerHelpers";

export type CADActionTypes =
    { type: "SET_MODEL", model: ICADModel, resetCurrentLayer: boolean } |
    { type: "SET_HOVERED_OPERATION", uniqueID: number | null, } |
    { type: "SET_SELECTED_OPERATION", uniqueID: number | null, } |
    { type: "SET_UNIT_SET_ID", unitSetID: number, } |
    { type: "ADD_OPERATION_LIBRARY", libraryKey:string, libraryItems: ICADOperationLibraryItem[] } |
    { type: "SET_OP_EDITOR", editor: ICADOperationEditor | null, } |
    { type: "SET_OP_EDITOR_LOADING", loading: boolean, } |
    { type: "SET_CURRENT_TAB", tab: string, } |
    { type: "SET_CURRENT_LAYER", layer: string, } |
    { type: "SET_ACTIVE_EDGE_ADJUSTMENT", edgeAdjustment: EdgeAdjustmentWithoutID | null } |
    { type: "SET_EDGE_TOOL_STATE", edgeToolState: AddEdgeAdjustmentStateEnum };

const cadReducer: React.Reducer<ICADState, CADActionTypes> = (state, action) => {
    switch (action.type) {

        case "SET_UNIT_SET_ID":
            return {
                ...state, unitSetID: action.unitSetID,
            };

        case "SET_MODEL":
            if (action.resetCurrentLayer) {
                return {
                    ...state, model: action.model, currentLayer: action.model.layer,
                };
            }
            else {

                // If we are not resetting the current layer, we need to ensure that the current layer is updated with its current state
                const currentLayer = state.currentLayer ? GlassLayerHelpers.FindGlassLayerByName(action.model, state.currentLayer.name) : null;

                return {
                    ...state, model: action.model, currentLayer: currentLayer ?? action.model.layer,
                };
            }

        case "SET_HOVERED_OPERATION":
            return {
                ...state, hoveredOperationID: action.uniqueID,
            };

        case "SET_SELECTED_OPERATION":
            return {
                ...state, selectedOperationID: action.uniqueID, activeEdgeAdjustment: null
            };

        case "ADD_OPERATION_LIBRARY":
            return {
                ...state, operationLibraries: {
                    ...state.operationLibraries,
                    [action.libraryKey]: action.libraryItems
                }
            };

        case "SET_OP_EDITOR":
            return {
                ...state, operationEditor: action.editor, operationEditorLoading: false,
            };

        case "SET_EDGE_TOOL_STATE":
            return {
                ...state, edgeToolState: action.edgeToolState,
            };

        case "SET_OP_EDITOR_LOADING":
            return {
                ...state, operationEditorLoading: action.loading,
            };

        case "SET_ACTIVE_EDGE_ADJUSTMENT":
            return {
                ...state, activeEdgeAdjustment: action.edgeAdjustment, hoveredOperationID: null, selectedOperationID: null
            };

        case "SET_CURRENT_TAB":
            return {
                ...state, currentTab: action.tab,
            };

        case "SET_CURRENT_LAYER":

            // Find the desired layer. If it isn't found, return the top layer.
            const layer = FindLayer(state.model?.layer, action.layer) ?? state.model?.layer ?? null;
            return {
                ...state, currentLayer: layer,
            };



        default:
            return state;
    }
};

const FindLayer = (layer: ICADLayer | nullish, layerName: string): ICADLayer | null => {

    if (!layer) return null;
    if (layer.name === layerName) return layer;

    for (const childLayer of layer.layers) {
        const foundLayer = FindLayer(childLayer, layerName);
        if (foundLayer !== null) return foundLayer;
    }

    return null;
}

export default cadReducer;